// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type ReactElement,
  type ReactNode,
  type TableHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useReactTable,
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  flexRender,
  type ExpandedState,
  type VisibilityState,
  type PaginationState,
  type PaginationOptions,
  type SortingState,
  type TableOptions,
  type RowSelectionState,
  type RowPinningState,
  type Row,
} from '@tanstack/react-table';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { SortHeader } from './sort-header';
import { ColumnVisibilitySelector } from './column-visibility-selector';
import styles from './grouped-table.module.css';
import { Pagination } from './pagination';
import { InfoTooltip, type PopupsName } from '@shared/ui-kit/components';
import { gainLossSortingFn } from '@shared/ui-kit/utils';
import { useStateValue } from '@shared/ui-kit/providers/state-provider';
import { useTranslation } from 'react-i18next';

export type GroupedTableRow =
  | { subRows?: any[]; parent?: boolean }
  | Record<string, any>;

export interface GroupedTableProps<T extends GroupedTableRow>
  extends TableHTMLAttributes<HTMLTableElement> {
  readonly data: T[];
  readonly columns: ColumnDef<T, any>[];
  readonly initialExpanded?: ExpandedState;
  readonly manualPagination?: boolean;
  readonly onPaginationChange?: PaginationOptions['onPaginationChange'];
  readonly enablePagination?: boolean;
  readonly enableExpanding?: boolean;
  readonly pageCount?: number;
  readonly pageIndex?: number;
  readonly pageSize?: number;
  readonly emptyState?: ReactNode;
  readonly initialSorting?: SortingState;
  readonly initialRowSelection?: RowSelectionState;
  readonly enableRowSelection?: TableOptions<T>['enableRowSelection'];
  readonly enableMultiRowSelection?: TableOptions<T>['enableMultiRowSelection'];
  readonly onRowSelectionChange?: (value: T[]) => void;
  readonly getRowId?: TableOptions<T>['getRowId'];
  readonly disableDeselect?: boolean;
  readonly initialRowPinning?: RowPinningState;
  readonly keepPinnedRows?: boolean;
  readonly enableRowPinning?: boolean;
  readonly ['state-id']?: string;
  readonly onRowClick?: (row: Row<T>) => void;
}

export interface GroupedTableComponent {
  <T extends GroupedTableRow>(
    props: GroupedTableProps<T>,
    context?: any,
  ): ReactElement<any, any> | null;
}

const ROW_CLICK_IGNORE_SELECTOR =
  'button, a[href], input, select, textarea, label, [role="button"], [role="menuitem"], [data-stop-row-click]';

const shouldIgnoreRowClick = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest(ROW_CLICK_IGNORE_SELECTOR));

export const GroupedTable: GroupedTableComponent = ({
  data,
  columns,
  initialExpanded,
  manualPagination = false,
  enablePagination,
  enableExpanding = true,
  pageCount,
  pageIndex,
  pageSize,
  onPaginationChange,
  onRowSelectionChange: onRowChange,
  emptyState,
  initialSorting,
  enableRowSelection = false,
  enableMultiRowSelection = false,
  initialRowSelection,
  disableDeselect = false,
  getRowId,
  enableRowPinning,
  initialRowPinning,
  keepPinnedRows = false,
  'state-id': stateId = 'grouped-table',
  onRowClick,
  ...props
}) => {
  const { t } = useTranslation();
  const [columnsState] = useStateValue<VisibilityState>(stateId);

  const isMounted = useRef(false);

  const [expanded, setExpanded] = useState<ExpandedState>(
    () => initialExpanded ?? {},
  );

  const initialColumnVisibility: VisibilityState = useMemo(
    () =>
      columns.reduce(
        (state, column: any) =>
          column.meta?.initiallyHidden
            ? { ...state, [column.accessorKey ?? column?.id]: false }
            : columnsState ?? state,
        {} as VisibilityState,
      ),
    [columns, columnsState],
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility,
  );
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: pageIndex ?? 0,
    pageSize: enablePagination ? pageSize ?? 50 : data.length,
  }));

  const [sorting, setSorting] = useState<SortingState>(() => initialSorting ?? []);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    () => initialRowSelection ?? {},
  );

  const [rowPinning, setRowPinning] = useState<RowPinningState>(
    initialRowPinning ?? {
      top: [],
      bottom: [],
    },
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSubRows: row => (Array.isArray(row?.subRows) ? row?.subRows : []),
    defaultColumn: {
      minSize: 36,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
    state: {
      expanded,
      columnVisibility,
      pagination,
      sorting,
      rowSelection,
      rowPinning,
    },
    enableExpanding: enableExpanding,
    onExpandedChange: setExpanded,
    onPaginationChange: state => {
      setPagination(state);
      onPaginationChange?.(state);
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onRowPinningChange: setRowPinning,
    paginateExpandedRows: manualPagination,
    manualPagination: manualPagination,
    pageCount: manualPagination ? pageCount : undefined,
    enableRowSelection,
    enableMultiRowSelection,
    keepPinnedRows,
    enableRowPinning,
    getRowId,
    sortingFns: {
      gainLoss: gainLossSortingFn,
    },
  });

  useEffect(() => {
    if (enableRowSelection) setRowSelection(() => initialRowSelection ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  useEffect(() => {
    if (!manualPagination && !enablePagination) {
      setPagination(prev => ({
        ...prev,
        pageSize: data.length,
      }));
    }
  }, [data.length, enablePagination, manualPagination]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;

      return;
    }

    if (enableRowSelection && onRowChange && rowSelection !== initialRowSelection) {
      const value = table.getSelectedRowModel().rows.map(row => row.original);

      onRowChange?.(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, enableRowSelection]);

  const renderRow = useCallback(
    <T extends GroupedTableRow>(row: Row<T>) => {
      const isRowInteractive =
        Boolean(onRowClick) ||
        row.getCanExpand() ||
        (enableRowSelection && row.getCanSelect());

      return (
        <tr
          key={row.id}
          aria-expanded={
            row.getCanExpand() ? row.getIsExpanded() : row.original.parent
          }
          className={classNames(styles.tr, {
            [styles.animate]: !row.getCanExpand(),
          })}
          onClick={e => {
            if (shouldIgnoreRowClick(e.target)) {
              return;
            }

            onRowClick?.(row as Row<any>);

            if (row.getCanExpand()) {
              row.getToggleExpandedHandler()?.();
            } else if (row.getCanSelect()) {
              if (disableDeselect && row.getIsSelected()) {
                setRowSelection(prev => ({ ...prev, [row.id]: true }));
              } else {
                row.getToggleSelectedHandler()?.(e);
              }
            }
          }}
          aria-selected={row.getCanSelect() ? row.getIsSelected() : undefined}
          data-testid={`row-${row.id}`}
          aria-readonly={!isRowInteractive}
          data-interactive={isRowInteractive || undefined}
          data-depth={row.depth}
          data-pinned={!!row.getIsPinned()}
        >
          {row.getVisibleCells().map(cell => (
            <td
              key={cell.id}
              className={classNames(cell.column.columnDef.meta?.className)}
              style={{
                minWidth:
                  cell.column.columnDef.minSize === Number.MAX_SAFE_INTEGER
                    ? undefined
                    : (cell.column.columnDef.minSize &&
                        `${cell.column.columnDef.minSize / 16}rem`) ||
                      undefined,
                width:
                  cell.column.getSize() === Number.MAX_SAFE_INTEGER
                    ? undefined
                    : `${cell.column.getSize() / 16}rem`,
                maxWidth:
                  cell.column.columnDef.maxSize === Number.MAX_SAFE_INTEGER
                    ? undefined
                    : (cell.column.columnDef.maxSize &&
                        `${cell.column.columnDef.maxSize / 16}rem`) ||
                      undefined,
              }}
              data-testid={`row-${row.id}-cell-${cell.id}`}
            >
              <div
                className={classNames({
                  'justify-end': cell.column.columnDef.meta?.alignment === 'end',
                  'justify-start':
                    cell.column.columnDef.meta?.alignment === 'start',
                  'justify-center':
                    cell.column.columnDef.meta?.alignment === 'center',
                })}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            </td>
          ))}
        </tr>
      );
    },
    [disableDeselect, enableRowSelection, onRowClick],
  );

  return (
    <table {...props} className={classNames(styles.table, props.className)}>
      <thead>
        {data.length > 0 ? (
          table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className={styles.tr}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className={classNames(
                    styles.th,
                    { 'cursor-pointer': header.column.getCanSort() },
                    header.column.columnDef.meta?.className,
                  )}
                  style={{
                    minWidth:
                      header.column.columnDef.minSize === Number.MAX_SAFE_INTEGER
                        ? undefined
                        : (header.column.columnDef.minSize &&
                            `${header.column.columnDef.minSize / 16}rem`) ||
                          undefined,
                    width:
                      header.getSize() === Number.MAX_SAFE_INTEGER
                        ? undefined
                        : `${header.getSize() / 16}rem`,
                    maxWidth:
                      header.column.columnDef.maxSize === Number.MAX_SAFE_INTEGER
                        ? undefined
                        : (header.column.columnDef.maxSize &&
                            `${header.column.columnDef.maxSize / 16}rem`) ||
                          undefined,
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                  data-testid={`header-${header.id}`}
                >
                  <div
                    className={classNames({
                      'justify-end':
                        header.column.columnDef.meta?.alignment === 'end' ||
                        header.column.columnDef.meta?.showColumnVisibilitySelector,
                      'justify-start':
                        header.column.columnDef.meta?.alignment === 'start',
                      'justify-center':
                        header.column.columnDef.meta?.alignment === 'center',
                    })}
                  >
                    {header.isPlaceholder ? null : header.column.columnDef.meta
                        ?.tooltip ? (
                      <InfoTooltip
                        title={header.column.columnDef.meta.tooltip as PopupsName}
                      >
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                      </InfoTooltip>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                    <SortHeader sort={header.column} />

                    {header.column.columnDef.meta?.showColumnVisibilitySelector &&
                      !header.column.getCanSort() && (
                        <ColumnVisibilitySelector
                          columns={table.getAllLeafColumns()}
                          onColumnVisibilityChange={setColumnVisibility}
                          state-id={stateId}
                        />
                      )}
                  </div>
                </th>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <th
              colSpan={columns.length}
              className="h-6"
              aria-roledescription="empty state header"
            ></th>
          </tr>
        )}
      </thead>
      <tbody className={styles.tbody}>
        {data.length === 0 ? (
          <tr>
            <td colSpan={columns.length} aria-roledescription="empty state body">
              <div className={styles['empty-state']}>
                {emptyState ?? <span>{t('common.no-data', { ns: 'common' })}</span>}
              </div>
            </td>
          </tr>
        ) : (
          <>
            {table.getTopRows().map(renderRow)}
            {table.getCenterRows().map(renderRow)}
            {table.getBottomRows().map(renderRow)}
          </>
        )}
      </tbody>
      <tfoot>
        {table.getFooterGroups().map(footerGroup => (
          <tr
            key={footerGroup.id}
            className={styles.tr}
            data-testid={`footer-${footerGroup.id}`}
          >
            {footerGroup.headers.map(cell => (
              <th
                key={cell.id}
                colSpan={cell.colSpan}
                className={cell.column.columnDef.meta?.className}
                style={{
                  minWidth:
                    cell.column.columnDef.minSize === Number.MAX_SAFE_INTEGER
                      ? undefined
                      : (cell.column.columnDef.minSize &&
                          `${cell.column.columnDef.minSize / 16}rem`) ||
                        undefined,
                  width:
                    cell.column.getSize() === Number.MAX_SAFE_INTEGER
                      ? undefined
                      : `${cell.column.getSize() / 16}rem`,
                  maxWidth:
                    cell.column.columnDef.maxSize === Number.MAX_SAFE_INTEGER
                      ? undefined
                      : (cell.column.columnDef.maxSize &&
                          `${cell.column.columnDef.maxSize / 16}rem`) ||
                        undefined,
                }}
                data-testid={`footer-${footerGroup.id}-cell-${cell.id}`}
              >
                <div>
                  {cell.isPlaceholder
                    ? null
                    : flexRender(cell.column.columnDef.footer, cell.getContext())}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </tfoot>
      {enablePagination && data.length > 0 ? (
        <tfoot>
          <tr>
            <td
              colSpan={table.getAllColumns().length}
              aria-roledescription="pagination"
            >
              <Pagination
                pageIndex={table.getState().pagination.pageIndex}
                pageSize={table.getState().pagination.pageSize}
                pageCount={table.getPageCount()}
                canPreviousPage={table.getCanPreviousPage()}
                canNextPage={table.getCanNextPage()}
                onNextPage={table.nextPage}
                onPreviousPage={table.previousPage}
              />
            </td>
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
};
